<template>
  <!-- [sc-48583 / task 48586] Il banner dice che la prova è finita; questa
       dice cosa farne. Compare una volta per sessione: il gestionale resta
       consultabile apposta, e una finestra che torna a ogni pagina sarebbe un
       ostacolo fra il cliente e i suoi dati. -->
  <VueFinalModal
    v-model="aperta"
    classes="documents-modal"
    content-class="archive-modal-content trial-ended"
  >
    <template #default>
      <h3 class="modal__title">🔒 Prova terminata</h3>
      <p>
        La tua prova è terminata. I tuoi dati sono ancora al sicuro e puoi
        continuare a consultarli. Per tornare a creare preventivi, gestire
        ordini, clienti e magazzino, attiva un abbonamento
        <strong>{{ piano }}</strong
        >.
      </p>
      <p>
        L'abbonamento è <strong>mensile e si rinnova automaticamente</strong>.
        Il costo è scritto nel contratto che ti manderemo via mail sotto tua
        richiesta.
      </p>
      <!-- L'avviso dei novanta giorni sta qui e non solo nelle email: chi
           legge questa finestra è esattamente chi rischia di perderli. -->
      <p class="trial-ended__warning">
        I dati restano consultabili per {{ retentionDays }} giorni dalla fine
        della prova<template v-if="deletionLabel">
          : il <strong>{{ deletionLabel }}</strong> vengono cancellati</template
        >. Scaricali adesso, così li hai comunque.
      </p>
      <p v-if="!canExport" class="trial-ended__note">
        Lo scarico dei dati lo fa l'amministratore dell'azienda.
      </p>

      <!-- Le uscite in colonna, una sotto l'altra e larghe uguali: sono tre
           strade diverse, non due pulsanti e un ripensamento, e in riga la
           piu' importante non si distingueva. -->
      <div class="trial-ended__choices">
        <span class="trial-ended__choices-title">Scegli come procedere</span>
        <button class="trial-ended__choice is-quiet" @click="chiudi">
          <i class="fa-solid fa-lock"></i>
          Continua in sola lettura
        </button>
        <a
          v-if="salesLink"
          class="trial-ended__choice is-primary"
          :href="salesLink"
        >
          <i class="fa-solid fa-bolt"></i>
          Attiva l'abbonamento
        </a>
        <!-- [sc-46314] Fra il comprare e l'andarsene c'e' il parlarne: qui
             il cliente ha appena letto che la prova e' finita, ed e' il
             momento in cui una call serve davvero. -->
        <a
          v-if="callLink"
          class="trial-ended__choice is-call"
          :href="callLink"
          target="_blank"
          rel="noopener"
        >
          <i class="fa-solid fa-calendar-check"></i>
          Prenota una call
        </a>
        <router-link
          v-if="canExport"
          class="trial-ended__choice is-soft"
          to="/home/dati"
          @click="chiudi"
        >
          <i class="fa-solid fa-download"></i>
          Scarica i tuoi dati
        </router-link>
      </div>
    </template>
  </VueFinalModal>
</template>

<script>
import { PLAN_START, RETENTION_DAYS } from "@/config/plans";
import { linkPrenotaCall, mailtoAttivazione } from "@/utilities/abbonamento";

/** Dove si segna che è già stata vista, per azienda. */
function chiave(companyId) {
  return `crmstart:fine-prova:${companyId || "?"}`;
}

/**
 * Già vista in questa sessione?
 *
 * `sessionStorage` può lanciare - finestre anonime, cookie di terze parti
 * bloccati - e allora si preferisce mostrarla di nuovo: farla vedere due volte
 * è un fastidio, non farla vedere mai è perdere l'unico avviso che dice al
 * cliente di portarsi via i dati.
 */
function giaVista(companyId) {
  try {
    return sessionStorage.getItem(chiave(companyId)) === "1";
  } catch {
    return false;
  }
}

function segna(companyId) {
  try {
    sessionStorage.setItem(chiave(companyId), "1");
  } catch {
    // Niente da fare: al prossimo caricamento ricomparirà.
  }
}

export default {
  name: "TrialEndedModal",
  data() {
    return { aperta: false };
  },
  computed: {
    trial() {
      return this.$store.getters["company/trial"];
    },
    readOnly() {
      return Boolean(this.trial?.readOnly);
    },
    utente() {
      return this.$store.state.currentUser;
    },
    companyId() {
      return this.utente?.companyId ?? null;
    },
    canExport() {
      // Stessa regola del banner e della rotta: i dati li scarica solo
      // l'amministratore.
      return this.utente?.isAdmin === true;
    },
    /**
     * [sc-48583] Solo START: qui si parla del piano che si sta provando, e
     * PLUS non si prova - si vende dopo una call. Nominarlo in una finestra
     * che offre un pulsante solo lascerebbe scegliere fra due cose di cui una
     * da qui non è raggiungibile.
     */
    piano() {
      return `CRM ${PLAN_START.name}`;
    },
    retentionDays() {
      return RETENTION_DAYS;
    },
    companyName() {
      const nome = this.$store.getters["company/branding"]?.name;
      return typeof nome === "string" ? nome.trim() : "";
    },
    salesLink() {
      return mailtoAttivazione(this.companyName);
    },
    /** [sc-46314] L'agenda commerciale: vuota se non e' configurata. */
    callLink() {
      return linkPrenotaCall();
    },
    /** La data in cui i dati spariscono, come la scrive il banner. */
    deletionLabel() {
      const quando = this.trial?.deletionAt;
      if (!quando) return "";
      const data = new Date(quando);
      if (Number.isNaN(data.getTime())) return "";
      return data.toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },
    /** Prova finita, sessione aperta, e non l'ha già vista. */
    daMostrare() {
      return Boolean(this.utente) && this.readOnly;
    },
  },
  watch: {
    daMostrare: {
      immediate: true,
      handler(mostrare) {
        // Il profilo arriva dopo il primo disegno: senza `immediate` e senza
        // watch la finestra non comparirebbe mai al caricamento, che è
        // l'unico momento in cui serve.
        if (!mostrare) return;
        if (giaVista(this.companyId)) return;
        this.aperta = true;
        segna(this.companyId);
      },
    },
  },
  methods: {
    chiudi() {
      this.aperta = false;
    },
  },
};
</script>

<style scoped>
.trial-ended__warning {
  padding: 10px 12px;
  border-radius: 6px;
  background: #fdecea;
  color: #8a1c1c;
}

.trial-ended__note {
  color: #64748b;
  font-size: 0.9rem;
}

.trial-ended__choices {
  display: grid;
  gap: 10px;
  margin-top: 4px;
  padding: 12px;
  border: 0.5px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
}

/* Il titolino con le due righe ai lati: dice che qui sotto si sceglie, e
   separa le uscite dal testo senza aggiungere un altro paragrafo. */
.trial-ended__choices-title {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #64748b;
  font-size: 0.85rem;
}

.trial-ended__choices-title::before,
.trial-ended__choices-title::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #e2e8f0;
}

.trial-ended__choice {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  padding: 12px 16px;
  border-radius: 8px;
  border: 0.5px solid transparent;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
}

.trial-ended__choice.is-quiet {
  border-color: #cbd5e1;
  background: #fff;
  color: #475569;
  font-weight: 500;
}

.trial-ended__choice.is-primary {
  background: var(--brand, #0077a8);
  color: var(--brand-ink, #fff);
}

/* Contorno e fondo bianco: e' una strada di pari livello con lo scarico dei
   dati, ma le due non devono sembrare lo stesso pulsante scritto due volte. */
.trial-ended__choice.is-call {
  border-color: var(--brand-on-light, var(--brand, #0077a8));
  background: #fff;
  color: var(--brand-on-light, var(--brand, #0077a8));
}

.trial-ended__choice.is-soft {
  border-color: var(--brand-on-light, var(--brand, #0077a8));
  background: rgba(var(--brand-rgb, 0, 119, 168), 0.08);
  color: var(--brand-on-light, var(--brand, #0077a8));
}
</style>
